import React, { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { FaTimesCircle } from "react-icons/fa";

import type { AdminSpreadTransaction } from "../../lib/api/adminTransactionsData";
import { formatAmount } from "../../utils/FormatPerson";
import {
  feesFlowBadgeClass,
  feesFlowLabel,
  feesUserEmail,
  feesUserName,
  formatFeesDate,
  swapStatusBadgeClass,
} from "./feesTransactionsTable";

type Props = {
  open: boolean;
  onClose: () => void;
  transaction: AdminSpreadTransaction | null;
};

function SectionCard({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-xl border border-[#2C363E] bg-[#161C22]/55 p-4 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.035)] sm:p-5">
      <h3 className="mb-4 flex items-center gap-2.5 text-[11px] font-bold uppercase tracking-[0.18em] text-[#869AA9]">
        <span
          className="h-px w-7 shrink-0 bg-gradient-to-r from-[#4FB0FF]/90 to-transparent"
          aria-hidden
        />
        {title}
      </h3>
      <div className="divide-y divide-[#23292F]">{children}</div>
    </section>
  );
}

function KV({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1 py-3.5 first:pt-0 last:pb-0 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
      <dt className="min-w-[7.5rem] shrink-0 text-[12px] font-medium text-[#7D8F9C]">
        {label}
      </dt>
      <dd className="min-w-0 flex-1 text-left text-[13px] font-medium leading-snug text-[#EEF2F6] [overflow-wrap:anywhere] sm:text-right">
        {value}
      </dd>
    </div>
  );
}

function CopyChip({ text, label }: { text: string; label: string }) {
  const [copied, setCopied] = useState(false);

  const onCopy = async () => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error("Could not copy");
    }
  };

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        void onCopy();
      }}
      className="inline-flex max-w-full items-center gap-2 rounded-lg border border-[#3A4852]/80 bg-[#0D1114]/80 px-2.5 py-1.5 text-left transition hover:border-[#4FB0FF]/40 hover:bg-[#1C2227]"
    >
      <span className="min-w-0 truncate font-mono text-[11px] leading-snug text-[#C6D7E4]">
        {text}
      </span>
      <span className="shrink-0 rounded border border-[#3A4852] px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-[#AEBCC6]">
        {copied ? "Done" : "Copy"}
      </span>
    </button>
  );
}

function IdValue({ id, label }: { id?: string; label: string }) {
  if (!id) return <>—</>;
  return (
    <div className="flex flex-col items-stretch gap-2 sm:items-end">
      <CopyChip text={id} label={label} />
    </div>
  );
}

const AdminFeeDetailModal = ({ open, onClose, transaction }: Props) => {
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const id = window.requestAnimationFrame(() => closeRef.current?.focus());
    return () => window.cancelAnimationFrame(id);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || !transaction) return null;

  const tx = transaction;
  const swap = tx.spreadSwap ?? {};
  const swapStatus = swap.status?.toUpperCase() || "—";
  const idShort =
    tx._id.length > 14 ? `${tx._id.slice(0, 8)}…${tx._id.slice(-4)}` : tx._id;
  const userId = tx.userId || tx.user?._id || "";

  return (
    <div className="fixed inset-0 z-[150] flex items-end justify-center p-0 font-manrope sm:items-center sm:p-6">
      <button
        type="button"
        aria-label="Close dialog"
        className="absolute inset-0 bg-black/70 backdrop-blur-[2px]"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="fee-detail-title"
        className="relative z-[151] flex max-h-[92vh] w-full max-w-xl min-w-0 flex-col overflow-hidden rounded-t-2xl border border-[#2C363E] bg-[#13181B] shadow-[0_-12px_48px_-16px_rgba(0,0,0,0.88)] sm:max-h-[90vh] sm:rounded-2xl sm:shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="shrink-0 border-b border-[#2C363E]/90 bg-gradient-to-br from-[#1a222d] via-[#141a20] to-[#13181B] px-4 pb-4 pt-4 sm:px-6 sm:pt-5">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1 space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className={`inline-flex rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${feesFlowBadgeClass(
                    tx.flow
                  )}`}
                >
                  {feesFlowLabel(tx.flow)}
                </span>
                <span
                  className={`inline-flex rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${swapStatusBadgeClass(
                    swap.status
                  )}`}
                >
                  TRX {swapStatus}
                </span>
              </div>
              <h2
                id="fee-detail-title"
                className="font-sora text-lg font-bold leading-snug text-white sm:text-xl"
              >
                Fee
                <span className="font-normal text-[#5C6B76]"> · </span>
                <span className="font-mono text-[15px] font-semibold text-[#C6D7E4]">
                  {idShort}
                </span>
              </h2>
              <p className="text-[12px] text-[#869AA9]">
                {formatFeesDate(tx.createdAt)}
              </p>
            </div>
            <button
              ref={closeRef}
              type="button"
              onClick={onClose}
              className="shrink-0 text-white"
              aria-label="Close"
            >
              <FaTimesCircle className="text-2xl" />
            </button>
          </div>
        </header>

        <div className="min-h-0 min-w-0 flex-1 overflow-y-auto overflow-x-hidden px-4 py-5 scrollbar-thin scrollbar-track-[#13181B] scrollbar-thumb-[#3A4852] sm:px-6">
          <div className="min-w-0 space-y-5">
            <div className="grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="min-w-0 overflow-hidden rounded-xl border border-[#2C363E] bg-gradient-to-b from-[#1C2227] to-[#161E25] p-4">
                <p className="text-[10px] font-bold uppercase tracking-wider text-[#617889]">
                  Fee (USDT)
                </p>
                <p className="mt-2 min-w-0 font-sora text-xl font-bold tabular-nums leading-snug text-[#BEE1FB] [overflow-wrap:anywhere] sm:text-2xl">
                  {tx.spreadUSDT != null ? formatAmount(tx.spreadUSDT) : "—"}
                </p>
              </div>
              <div className="min-w-0 overflow-hidden rounded-xl border border-[#2C363E]/80 bg-[#161E25]/60 p-4">
                <p className="text-[10px] font-bold uppercase tracking-wider text-[#617889]">
                  Trade
                </p>
                <p className="mt-2 min-w-0 font-sora text-xl font-bold tabular-nums leading-snug text-white [overflow-wrap:anywhere] sm:text-2xl">
                  {tx.amount != null ? formatAmount(tx.amount) : "—"}
                  {tx.asset ? (
                    <span className="ml-1 text-[13px] font-semibold text-[#869AA9]">
                      {tx.asset}
                    </span>
                  ) : null}
                </p>
              </div>
              <div className="min-w-0 overflow-hidden rounded-xl border border-[#2C363E]/80 bg-[#161E25]/60 p-4">
                <p className="text-[10px] font-bold uppercase tracking-wider text-[#617889]">
                  TRX received
                </p>
                <p className="mt-2 font-sora text-xl font-semibold tabular-nums text-[#E7EBEE] sm:text-2xl">
                  {swap.trxAmount ? formatAmount(swap.trxAmount) : "—"}
                </p>
              </div>
            </div>

            {swap.error ? (
              <div
                className="rounded-xl border border-red-500/35 bg-red-500/[0.08] px-4 py-3 text-[13px] text-red-100/95"
                role="alert"
              >
                <p className="text-[11px] font-bold uppercase tracking-wider text-red-300">
                  Swap error
                </p>
                <p className="mt-1 break-words">{swap.error}</p>
              </div>
            ) : null}

            <SectionCard title="User">
              <KV label="Name" value={feesUserName(tx)} />
              <KV label="Email" value={feesUserEmail(tx)} />
              <KV
                label="User ID"
                value={<IdValue id={userId} label="User ID" />}
              />
            </SectionCard>

            <SectionCard title="Trade">
              <KV label="Flow" value={feesFlowLabel(tx.flow)} />
              <KV label="Ledger type" value={tx.ledgerType || "—"} />
              <KV label="Asset" value={tx.asset || "—"} />
              <KV
                label="Trade ID"
                value={<IdValue id={tx.transactionId} label="Trade ID" />}
              />
              <KV
                label="Ledger entry ID"
                value={<IdValue id={tx._id} label="Ledger entry ID" />}
              />
            </SectionCard>

            <SectionCard title="USDT → TRX swap">
              <KV label="Status" value={swapStatus} />
              <KV
                label="Attempts"
                value={swap.attempts != null ? String(swap.attempts) : "—"}
              />
              <KV
                label="Obiex reference"
                value={
                  <IdValue id={swap.obiexReference} label="Obiex reference" />
                }
              />
              <KV label="Quote ID" value={swap.quoteId || "—"} />
              <KV label="Swapped at" value={formatFeesDate(swap.swappedAt)} />
            </SectionCard>
          </div>
        </div>

        <div className="shrink-0 border-t border-[#2C363E] bg-[#13181B] px-4 py-3 sm:px-6">
          <button
            type="button"
            onClick={onClose}
            className="flex h-11 w-full items-center justify-center rounded-xl border border-[#3A4852] text-[14px] font-bold text-[#E7EBEE] transition hover:bg-[#1C2227]"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminFeeDetailModal;
